import { ArrowLeft, ArrowRight, Eye, EyeOff, LoaderCircle } from "lucide-react";
import { useEffect, useReducer, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { PreferenceControls } from "@/components/app-settings/preference-controls";
import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { useAuth } from "@/components/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { buildLoginPayload, getLoginErrorMessage, getLoginFailureCode } from "@/lib/auth/login-form";
import { initialLoginState, loginReducer } from "@/lib/auth/login-state";
import { resolveAdminLogoSource } from "@/lib/branding/image-source";
import { ApiError } from "@/lib/client/api";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";

export function LoginPage() {
  const settings = useAppSettings();
  const { t, language, theme } = settings;
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, dispatch] = useReducer(loginReducer, initialLoginState);
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submissionPending = useRef(false);
  const from = getReturnPath(location.state);
  const text = language === "zh" ? loginText.zh : loginText.en;
  const busy = state.submitting || status === "loading";
  const error = state.error ? getLoginErrorMessage(state.error, language) : null;
  const logoSrc = resolveAdminLogoSource(settings, theme);

  useEffect(() => {
    if (status === "anonymous") {
      formRef.current?.querySelector<HTMLInputElement>(`#login-${state.step}`)?.focus();
    }
  }, [state.step, state.error, status]);

  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || submissionPending.current) return;
    formRef.current?.querySelector<HTMLInputElement>(`#login-${state.step}`)?.focus();

    if (state.step === "email") {
      dispatch({ type: "next" });
      return;
    }

    const payload = buildLoginPayload(state, language);

    if (!payload.ok) {
      dispatch({ type: "submit" });
      return;
    }

    // Block re-entrant submissions before React renders the disabled controls.
    submissionPending.current = true;
    dispatch({ type: "submit" });

    try {
      await login(payload.data);
      navigate(from, { replace: true });
    } catch (loginError) {
      dispatch({ type: "failed", error: getLoginFailureCode(loginError instanceof ApiError ? loginError.status : undefined) });
    } finally {
      submissionPending.current = false;
    }
  }

  return (
    <main className="flex min-h-svh items-center justify-center bg-muted/40 p-4 text-foreground sm:p-6">
      <section aria-labelledby="login-heading" className="w-full max-w-[400px] rounded-lg border border-border bg-background p-6 shadow-sm sm:p-8">
        <header className="mb-8 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <div className="flex h-9 min-w-0 items-center">
            {logoSrc && failedLogo !== logoSrc ? (
              <img
                key={logoSrc}
                src={logoSrc}
                alt="VZEN"
                referrerPolicy="no-referrer"
                className="h-8 w-24 max-w-full object-contain object-left"
                onError={() => setFailedLogo(logoSrc)}
              />
            ) : <span className="text-xl font-semibold">VZEN</span>}
          </div>
          <PreferenceControls />
        </header>
        <div className="mb-6">
          <h1 id="login-heading" className="text-2xl font-semibold">{t("login.submit")}</h1>
        </div>
        <form ref={formRef} noValidate aria-busy={busy} className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {state.step === "email" ? (
              <div className="space-y-2">
                <label htmlFor="login-email" className="block text-sm font-medium">{t("login.email")}</label>
                <Input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  disabled={busy}
                  aria-invalid={state.error === "invalidEmail"}
                  aria-describedby={error ? "login-error" : undefined}
                  value={state.email}
                  onChange={(event) => dispatch({ type: "email", value: event.target.value })}
                />
              </div>
            ) : (
              <>
                <div className="flex min-h-9 items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    title={text.back}
                    aria-label={text.back}
                    disabled={busy}
                    onClick={() => dispatch({ type: "back" })}
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <p className="min-w-0 break-all text-sm text-muted-foreground">{state.email}</p>
                  <input type="hidden" name="email" autoComplete="username" value={state.email} />
                </div>
                <div className="space-y-2">
                  <label htmlFor="login-password" className="block text-sm font-medium">{t("login.password")}</label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      name="password"
                      type={state.showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      disabled={busy}
                      className="pr-12"
                      aria-invalid={state.error === "missingPassword" || state.error === "invalidCredentials"}
                      aria-describedby={error ? "login-error" : undefined}
                      value={state.password}
                      onChange={(event) => dispatch({ type: "password", value: event.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      className="absolute right-0 top-0 h-full w-11 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      title={state.showPassword ? text.hidePassword : text.showPassword}
                      aria-label={state.showPassword ? text.hidePassword : text.showPassword}
                      aria-controls="login-password"
                      aria-pressed={state.showPassword}
                      disabled={busy}
                      onClick={() => dispatch({ type: "togglePassword" })}
                    >
                      {state.showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
          <div id="login-error" role="status" aria-live="polite" aria-atomic="true" className="min-h-10 text-sm text-destructive">
            {error}
          </div>
          <Button className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" type="submit" disabled={busy}>
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : null}
            {status === "loading" ? t("auth.loading") : state.submitting ? t("login.signingIn") : state.step === "email" ? text.next : t("login.submit")}
            {!busy && state.step === "email" ? <ArrowRight className="h-4 w-4" aria-hidden="true" /> : null}
          </Button>
        </form>
      </section>
    </main>
  );
}

const loginText = {
  en: { next: "Continue", back: "Back to email", showPassword: "Show password", hidePassword: "Hide password" },
  zh: { next: "继续", back: "返回邮箱", showPassword: "显示密码", hidePassword: "隐藏密码" },
};

function getReturnPath(state: unknown): string {
  if (state && typeof state === "object" && "from" in state) {
    const from = state.from;

    if (from && typeof from === "object" && "pathname" in from && typeof from.pathname === "string") {
      return from.pathname;
    }
  }

  return workbenchRoutes.dashboard;
}
